import { AllyCircularLoopProtection } from "./ally-circular-loop-protection.class";
import { AllyBase } from "./base";
import { AllyId } from "./ally-id.class";
import { AllyObject } from "./ally-object.class";
import { AllyTools } from "./ally-tools.class";

/**
 * Objects of this class house event listener.
 * 
 * @extends AllyBase
 * @see AllyEventListener
 */
export class AllyEventListenerInstance extends AllyBase {
    /**
     * This callback will remove this object from the AllyEventListener["eventListenerList"].
     */
    private remove: () => void;
    /**
     * The AllyObject object that manages the node that the event listener is listening on.
     */
    private object: AllyObject;
    /**
     * The id of this AllyEventListenerInstance object.
     * 
     * @see AllyId
     */
    private id: string;
    /**
     * The event listener will only run the callback when an event is triggered if this property is True.
     */
    private listen: boolean;
    /**
     * The event type that will set the AllyEventListenerInstance["listen"] property to True.
     */
    private startEvent: string;
    /**
     * The event type that will set the AllyEventListenerInstance["listen"] property to False.
     */
    private stopEvent: string;

    private deCircularLoopProtection: AllyCircularLoopProtection;

    private deTools: AllyTools;

    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @param ids The AllyId object the AllyEventListenerInstance object will use.
     * @param remove The remove callback that should be provided by the AllyEventListener object.
     * @param object The AllyObject object that manages the node that the event listener is listening on.
     * @param type The type of the event the event listener will listen to.
     * @param callback The callback to be run when an event occurs.
     * @see AllyBase
     */
    constructor(bridge: () => AllyBase, ids: AllyId, remove: AllyEventListener["remove"], object: AllyObject, type: string, callback: (event: Event) => void) {
        super(bridge);

        this.deCircularLoopProtection = new AllyCircularLoopProtection(this.bridge);

        this.deTools = new AllyTools();

        this.remove = () => {
            remove(this);
        };

        this.object = object;
        this.id = ids.getId();
        this.listen = true;
        let listener = (event: Event) => {
            if (this.listen && !AllyTools.paused) {
                this.runOutside(() => {
                    this.deCircularLoopProtection.checkForCircularLoop(this.object.id, this.id);
                    callback(event);
                });
            }
        }
        this.object.node.addEventListener(type, listener);
        let startListener = () => {
            this.listen = true;
        }
        let stopListener = () => {
            this.listen = false;
        }
        this.startEvent = this.id + ":start";
        this.stopEvent = this.id + ":stop";
        this.object.node.addEventListener(this.startEvent, startListener);
        this.object.node.addEventListener(this.stopEvent, stopListener);
        this.destroyer.addCallback(() => {
            this.object.node.removeEventListener(type, listener);
            this.object.node.removeEventListener(this.startEvent, startListener);
            this.object.node.removeEventListener(this.stopEvent, stopListener);
            object.library.angular.cleanUpListener(this.object.node, this.startEvent);
            object.library.angular.cleanUpListener(this.object.node, this.stopEvent);
            ids.returnId(this.id);
            this.remove();
        });
        let toolsCallback = this.deTools.set(this.object.id, this);
        this.destroyer.addCallback(() => {
            toolsCallback();
        });
    }
    /**
     * Events that are caused by the callback provided will be ignored by the event listener of this AllyEventListenerInstance object.
     * @param callback The callback to be provided.
     */
    public runOutside(callback: () => void) {
        this.object.node.dispatchEvent(new CustomEvent(this.stopEvent));
        callback();
        this.object.node.dispatchEvent(new CustomEvent(this.startEvent));
    }
}

/**
 * This class is used to create event listener.
 * 
 * @extends AllyBase
 * @see AllyEventListenerInstance
 */
export class AllyEventListener extends AllyBase {
    /**
     * The list of all currently created event listener that were created by this AllyEventListener object.
     * 
     * @see AllyEventListenerInstance
     */
    public eventListenerList: AllyEventListenerInstance[];
    /**
     * The AllyId object of this AllyEventListener object.
     * 
     * @see AllyId
     */
    private ids: AllyId = new AllyId("event-listener", this.bridge);
    /**
     * The remove callback that will be provided to the created AllyEventListenerInstance.
     */
    private remove: (eventListener: AllyEventListenerInstance) => void
    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @see AllyBase
     */
    constructor(bridge: () => AllyBase) {
        super(bridge);
        this.eventListenerList = [];
        this.remove = (eventListener) => {
            let index = this.eventListenerList.indexOf(eventListener);
            if (index !== -1) {
                this.eventListenerList.splice(index, 1);
            }
        };
    }
    /**
     * Creates an AllyEventListenerInstance object to contain the event listener.
     * @param object The AllyObject object on whose node the event listener will listen.
     * @param type The type of the event the event listener will listen to.
     * @param callback The callback to be run when an event occurs.
     * @param options 
     * Options object - Available options:
     * runOutside - If this options is set to false the event listener will be abled to react to events caused by the callback of the event listener. This may create an infinit loop.
     * @returns Returns the created AllyEventListenerInstance object.
     */
    public createEventListener(object: AllyObject, type: string, callback: (event: Event) => void, options?: {runOutside?: boolean}) {
        let eventlistener = new AllyEventListenerInstance(this.bridge, this.ids, this.remove, object, type, callback);
        this.eventListenerList.push(eventlistener);
        return eventlistener;
    }
}